import { useCallback, useEffect, useState } from "react";

export type PushState = "unsupported" | "blocked" | "not-enabled" | "enabled" | "pending";

/** Web Push (and the service worker + Notification API it relies on) is only
 *  available in a secure context with all three primitives present. iOS Safari
 *  only exposes them once the site is installed to the home screen as a PWA. */
function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Decode a base64url VAPID key into the Uint8Array applicationServerKey
 *  expects. base64url uses -/_ and omits padding, so translate to standard
 *  base64 and pad before atob. */
function urlBase64ToUint8Array(base64url: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/** Read a PushSubscription key as a base64url string. getKey returns an
 *  ArrayBuffer or null (no `as` cast needed — narrow the null). */
function encodeKey(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function fetchVapidPublicKey(): Promise<string | null> {
  const res = await fetch("/api/push/vapid-public-key");
  if (!res.ok) return null;
  const data = (await res.json()) as { publicKey?: string };
  return typeof data.publicKey === "string" ? data.publicKey : null;
}

async function saveSubscription(sub: PushSubscription): Promise<void> {
  const res = await fetch("/api/push/subscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      endpoint: sub.endpoint,
      p256dh: encodeKey(sub.getKey("p256dh")),
      auth: encodeKey(sub.getKey("auth")),
      userAgent: navigator.userAgent,
    }),
  });
  if (!res.ok) throw new Error(`save subscription failed: ${res.status}`);
}

async function deleteSubscription(endpoint: string): Promise<void> {
  const res = await fetch("/api/push/subscribe", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ endpoint }),
  });
  if (!res.ok) throw new Error(`delete subscription failed: ${res.status}`);
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const registration = await navigator.serviceWorker.getRegistration("/sw.js");
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

/**
 * Close any browser notifications THIS device is showing for `instanceId` —
 * called when the user reads that conversation in-app, so the notification for a
 * message they just read clears on the same device. This is the foreground half
 * of read-dismiss; the server sends a "read" push for the cross-device (app
 * closed elsewhere) half. Best-effort — a no-op without a service worker.
 */
export async function dismissLocalNotifications(instanceId: string): Promise<void> {
  try {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const registration = await navigator.serviceWorker.getRegistration("/sw.js");
    if (!registration) return;
    const notes = await registration.getNotifications();
    for (const note of notes) {
      const data = note.data as { instanceId?: string } | null;
      if (data?.instanceId === instanceId) note.close();
    }
  } catch {
    // Best-effort — never let notification cleanup break the read flow.
  }
}

/**
 * On app foreground / load, close any showing notifications for conversations
 * that are no longer unread. This catches the case we can't push-dismiss on iOS:
 * a notification lands on the phone, the conversation is read on another device
 * while the phone's app is closed, then the user opens the phone app — the stale
 * notification is swept here (a cold open carries fresh server-rendered unread
 * counts). `isUnread(instanceId)` reports whether that agent still has unread.
 * Best-effort; a no-op without a service worker.
 */
export async function reconcileNotifications(isUnread: (instanceId: string) => boolean): Promise<void> {
  try {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const registration = await navigator.serviceWorker.getRegistration("/sw.js");
    if (!registration) return;
    const notes = await registration.getNotifications();
    for (const note of notes) {
      const data = note.data as { instanceId?: string } | null;
      // Close only when we're confident it's read (unread === 0); an unknown /
      // still-unread instance is left alone, so a stale map never wrongly clears.
      if (data?.instanceId && !isUnread(data.instanceId)) note.close();
    }
  } catch {
    // Best-effort — never let notification cleanup break foregrounding.
  }
}

function deriveState(subscribed: boolean): PushState {
  if (!isPushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  return subscribed ? "enabled" : "not-enabled";
}

/** iOS/iPadOS detection. iPadOS 13+ masquerades as desktop Safari
 *  ("Macintosh" UA), so a Mac UA with a touch screen counts as iPad. */
export function isIosDevice(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
}

/** Android detection — for the "Install as app" promotion, which uses the
 *  Chromium `beforeinstallprompt` API (Android/desktop Chromium) rather than
 *  iOS's manual steps. */
export function isAndroidDevice(): boolean {
  return /Android/.test(navigator.userAgent);
}

/** Installed-to-Home-Screen detection: the standard display-mode media query,
 *  plus the legacy `navigator.standalone` flag older iOS Safari sets instead. */
export function isStandaloneDisplay(): boolean {
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  const legacyStandalone: unknown = Reflect.get(navigator, "standalone");
  return legacyStandalone === true;
}

/**
 * Platform facts the notification UIs branch on: whether this is an
 * iOS device (where push only works once the site is installed to the Home
 * Screen) and whether we are running as an installed app. Detected on mount —
 * both default false during SSR/first paint, so server and client render the
 * same initial tree (no hydration mismatch).
 */
export function usePwaPlatform(): { isIos: boolean; isAndroid: boolean; isStandalone: boolean } {
  const [platform, setPlatform] = useState({ isIos: false, isAndroid: false, isStandalone: false });

  useEffect(() => {
    let active = true;
    void Promise.resolve().then(() => {
      if (active) setPlatform({ isIos: isIosDevice(), isAndroid: isAndroidDevice(), isStandalone: isStandaloneDisplay() });
    });
    return () => {
      active = false;
    };
  }, []);

  return platform;
}

/**
 * Per-account/per-device browser-push opt-in. Reflects the live state
 * (unsupported / blocked / not-enabled / enabled) and exposes enable/disable
 * actions that drive the full permission → subscribe → persist flow.
 */
export function usePushSubscription(): {
  state: PushState;
  error: string | null;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
} {
  // Start "pending" until the live subscription/permission check resolves: the
  // real state is unknown on the first render, and treating that window as
  // "not-enabled" would let consumers act on an unverified default.
  const [state, setState] = useState<PushState>("pending");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!isPushSupported()) {
      void Promise.resolve().then(() => {
        if (active) setState("unsupported");
      });
      return () => {
        active = false;
      };
    }
    void currentSubscription().then((sub) => {
      if (active) setState(deriveState(sub !== null));
    }).catch(() => {
      // Safari lets users deny the Service Worker API wholesale ("Never for
      // this website"); getRegistration then REJECTS instead of resolving
      // null. That is a browser privacy setting, not a fault — surface it as
      // "blocked" (same UI as denied notifications) instead of letting the
      // rejection escape as an unhandled-rejection client-error report
      // (prod: ~10/day from one Safari user, 2026-09-01).
      if (active) setState("blocked");
    });
    return () => {
      active = false;
    };
  }, []);

  const enable = useCallback(async () => {
    setError(null);
    if (!isPushSupported()) {
      setState("unsupported");
      return;
    }
    setState("pending");
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState(permission === "denied" ? "blocked" : "not-enabled");
        return;
      }
      const publicKey = await fetchVapidPublicKey();
      if (!publicKey) {
        setError("Browser notifications are not available right now.");
        setState("not-enabled");
        return;
      }
      const registration = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }));
      try {
        await saveSubscription(subscription);
      } catch (err) {
        // Roll the local subscription back so it doesn't dangle without a
        // stored row the runtime can deliver to (only if we created it here).
        if (!existing) await subscription.unsubscribe().catch(() => undefined);
        throw err;
      }
      setState("enabled");
    } catch {
      setError("Could not enable browser notifications.");
      setState(deriveState(false));
    }
  }, []);

  const disable = useCallback(async () => {
    setError(null);
    setState("pending");
    try {
      const subscription = await currentSubscription();
      if (subscription) {
        await deleteSubscription(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setState(deriveState(false));
    } catch {
      setError("Could not disable browser notifications.");
      setState(deriveState(true));
    }
  }, []);

  return { state, error, enable, disable };
}
